import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { Briefcase, Search, Smile, Users, type LucideIcon } from "lucide-react";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "cmdk";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/shared/ui/dialog";
import { LoadingSpinner } from "@/shared/ui/loadingSpinner";
import {
  useReadClientsApiClientsGet,
  useReadContactsApiContactsGet,
  useReadProjectsApiProjectsGet,
} from "@/type-gen/api";
import type { Contact } from "@/type-gen/api";

const RESULT_PAGE_SIZE = 6;
const GROUP_HEADING_CLASS =
  "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground";

function joinDetail(parts: (string | null | undefined)[]): string | undefined {
  const present: string[] = [];
  for (const part of parts) {
    if (part && part.trim()) {
      present.push(part);
    }
  }
  if (present.length === 0) {
    return undefined;
  }
  return present.join(" · ");
}

function contactTitle(contact: Contact): string {
  if (contact.name && contact.name.trim()) {
    return contact.name;
  }
  if (contact.email && contact.email.trim()) {
    return contact.email;
  }
  return "Untitled contact";
}

function contactSearchQuery(contact: Contact): string | undefined {
  if (contact.name && contact.name.trim()) {
    return contact.name.trim();
  }
  if (contact.email && contact.email.trim()) {
    return contact.email.trim();
  }
  return undefined;
}

interface ResultItemProps {
  value: string;
  icon: LucideIcon;
  title: string;
  subtitle?: string | undefined;
  onSelect: (value: string) => void;
}

const ResultItem: React.FC<ResultItemProps> = ({
  value,
  icon: Icon,
  title,
  subtitle,
  onSelect,
}) => {
  return (
    <CommandItem
      value={value}
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{title}</span>
        {subtitle ? (
          <span className="block truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
        ) : null}
      </span>
    </CommandItem>
  );
};

interface GlobalSearchProps {
  variant?: "icon" | "bar";
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  variant = "icon",
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 300);
  const search = debouncedQuery.trim() ? debouncedQuery.trim() : null;

  const listParams = {
    page: 1,
    page_size: RESULT_PAGE_SIZE,
    search,
  };

  const queryOptions = {
    query: {
      enabled: open,
      placeholderData: keepPreviousData,
    },
  };

  const projectsQuery = useReadProjectsApiProjectsGet(listParams, queryOptions);
  const clientsQuery = useReadClientsApiClientsGet(listParams, queryOptions);
  const contactsQuery = useReadContactsApiContactsGet(listParams, queryOptions);

  const projects = projectsQuery.data?.data?.items ?? [];
  const clients = clientsQuery.data?.data?.items ?? [];
  const contacts = contactsQuery.data?.data?.items ?? [];
  const projectTotal = projectsQuery.data?.data?.total ?? 0;
  const clientTotal = clientsQuery.data?.data?.total ?? 0;
  const contactTotal = contactsQuery.data?.data?.total ?? 0;

  const isLoading =
    open &&
    (projectsQuery.isLoading ||
      clientsQuery.isLoading ||
      contactsQuery.isLoading);
  const hasResults =
    projects.length > 0 || clients.length > 0 || contacts.length > 0;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  const handleSelect = (value: string) => {
    if (value === "list:projects") {
      handleOpenChange(false);
      navigate("/projects");
      return;
    }
    if (value === "list:clients") {
      handleOpenChange(false);
      navigate("/clients");
      return;
    }
    if (value === "list:contacts") {
      handleOpenChange(false);
      navigate("/contacts");
      return;
    }
    if (value.startsWith("project:")) {
      const id = value.slice("project:".length);
      handleOpenChange(false);
      navigate(`/projects/${id}/shotlist`);
      return;
    }
    if (value.startsWith("client:")) {
      const id = value.slice("client:".length);
      handleOpenChange(false);
      navigate(`/clients/${id}`);
      return;
    }
    if (value.startsWith("contact:")) {
      const id = value.slice("contact:".length);
      const contact = contacts.find((item) => item.id === id);
      const contactQuery = contact ? contactSearchQuery(contact) : undefined;
      handleOpenChange(false);
      if (contactQuery) {
        navigate({
          pathname: "/contacts",
          search: `?q=${encodeURIComponent(contactQuery)}`,
        });
      } else {
        navigate("/contacts");
      }
    }
  };

  return (
    <>
      {variant === "bar" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Search projects, team members, and clients"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex w-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">Search</span>
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline">
            ⌘K
          </kbd>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Search projects, team members, and clients"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
        >
          <Search className="h-4 w-4" />
        </button>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="top-[12vh] max-h-[80vh] max-w-xl translate-y-0 gap-0 overflow-hidden p-0 sm:top-[12vh] sm:translate-y-0 [&>button]:hidden">
          <DialogTitle className="sr-only">
            Search projects, team members, and clients
          </DialogTitle>
          <DialogDescription className="sr-only">
            Find a project, team member, or client and jump to it.
          </DialogDescription>
          <Command
            shouldFilter={false}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="Search projects, team members, or clients"
                className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <CommandList className="min-h-0 flex-1 overflow-y-auto p-2 pb-3 scrollbar-styled">
              {isLoading && !hasResults ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                  <LoadingSpinner size="sm" />
                  Searching…
                </div>
              ) : null}

              {!isLoading || hasResults ? (
                <>
                  <CommandGroup
                    heading="Projects"
                    className={GROUP_HEADING_CLASS}
                  >
                    {projects.map((project) => (
                      <ResultItem
                        key={project.id}
                        value={`project:${project.id}`}
                        icon={Briefcase}
                        title={project.name}
                        subtitle={project.client.name}
                        onSelect={handleSelect}
                      />
                    ))}
                    {projects.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        {search ? "No matching projects" : "No projects yet"}
                      </div>
                    ) : null}
                    <CommandItem
                      value="list:projects"
                      onSelect={handleSelect}
                      className="cursor-pointer rounded-lg px-3 py-2 text-sm text-muted-foreground data-[selected=true]:bg-accent data-[selected=true]:text-foreground"
                    >
                      {projectTotal > projects.length
                        ? `View all ${projectTotal} projects`
                        : "View all projects"}
                    </CommandItem>
                  </CommandGroup>

                  <CommandGroup
                    heading="Clients"
                    className={`mt-1 ${GROUP_HEADING_CLASS}`}
                  >
                    {clients.map((client) => (
                      <ResultItem
                        key={client.id}
                        value={`client:${client.id}`}
                        icon={Smile}
                        title={client.name}
                        subtitle={client.description ?? undefined}
                        onSelect={handleSelect}
                      />
                    ))}
                    {clients.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        {search ? "No matching clients" : "No clients yet"}
                      </div>
                    ) : null}
                    <CommandItem
                      value="list:clients"
                      onSelect={handleSelect}
                      className="cursor-pointer rounded-lg px-3 py-2 text-sm text-muted-foreground data-[selected=true]:bg-accent data-[selected=true]:text-foreground"
                    >
                      {clientTotal > clients.length
                        ? `View all ${clientTotal} clients`
                        : "View all clients"}
                    </CommandItem>
                  </CommandGroup>

                  <CommandGroup
                    heading="Team members"
                    className={`mt-1 ${GROUP_HEADING_CLASS}`}
                  >
                    {contacts.map((contact) => (
                      <ResultItem
                        key={contact.id}
                        value={`contact:${contact.id}`}
                        icon={Users}
                        title={contactTitle(contact)}
                        subtitle={joinDetail([
                          contact.role,
                          contact.company,
                          contact.name && contact.email
                            ? contact.email
                            : undefined,
                        ])}
                        onSelect={handleSelect}
                      />
                    ))}
                    {contacts.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        {search
                          ? "No matching team members"
                          : "No team members yet"}
                      </div>
                    ) : null}
                    <CommandItem
                      value="list:contacts"
                      onSelect={handleSelect}
                      className="cursor-pointer rounded-lg px-3 py-2 text-sm text-muted-foreground data-[selected=true]:bg-accent data-[selected=true]:text-foreground"
                    >
                      {contactTotal > contacts.length
                        ? `View all ${contactTotal} team members`
                        : "View all team members"}
                    </CommandItem>
                  </CommandGroup>
                </>
              ) : null}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
};
